import { Dispatch, SetStateAction, useMemo } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";

import { ProductFormTransportStage, ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import type { ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import { getPackagingTransportLineScoreKey, getProductStageTransportLineScoreKey } from "../sectionScores";
import {
    ProductTransportInlineSelector,
    type TransportMaterializationPendingChange,
} from "./ProductTransportInlineSelector";

/** Product-level transport legs rendered in the dedicated Transport section (Figma `13631:68673`). */
const PRODUCT_TRANSPORT_STAGES: ProductFormTransportStage[] = [
    "product_to_storage",
    "storage_to_retail",
    "retail_to_consumer",
];

type Step6TransportProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    disabled?: boolean;
    transportLineScores?: Record<string, string | null>;
    resolvedTransports?: ProductLivePreview["resolvedTransports"];
    scoreUnit?: string;
    onTransportMaterializationPendingChange?: TransportMaterializationPendingChange;
};

const stageLabel = (stage: ProductFormTransportStage, t: TFunction): string => {
    switch (stage) {
        case "product_to_storage":
            return t("product.v2.form.transport.stage.product_to_storage", "Transport of product to storage");
        case "storage_to_retail":
            return t("product.v2.form.transport.stage.storage_to_retail", "Transport from storage to retail");
        case "retail_to_consumer":
            return t("product.v2.form.transport.stage.retail_to_consumer", "Transport from retail to consumer");
        default:
            return stage.replace(/_/g, " ");
    }
};

const resolveStageLineScore = (
    stage: ProductFormTransportStage,
    transportLineScores: Record<string, string | null>,
): string | null => {
    return transportLineScores[getProductStageTransportLineScoreKey(stage)] ?? null;
};

const resolveStageResolvedTransport = (
    stage: ProductFormTransportStage,
    resolvedTransports: ProductLivePreview["resolvedTransports"] | undefined,
) =>
    resolvedTransports?.find(
        (transport) => transport.stage === stage && transport.lineKey === getProductStageTransportLineScoreKey(stage),
    );

const packagingName = (packaging: ProductFormType["productPackagings"][number]): string =>
    packaging.packaging?.name ?? packaging.packagingReference?.name ?? "";

const packagingKey = (packaging: ProductFormType["productPackagings"][number], index: number): string =>
    packaging["@id"] ??
    packaging.packaging?.["@id"] ??
    packaging.packagingReference?.["@id"] ??
    packaging.packagingReference?.compositeId ??
    `packaging-${index}`;

/**
 * Wizard transport step (Figma `13631:68673`).
 *
 * Renders one fixed row per enabled product-level stage plus one row per persisted
 * packaging leg, with a route picker and per-leg score column.
 */
export const Step6Transport = ({
    product,
    setProduct,
    disabled = false,
    transportLineScores = {},
    resolvedTransports = [],
    scoreUnit = "Pts",
    onTransportMaterializationPendingChange,
}: Step6TransportProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isStageEnabled } = useEnabledTransportStages();

    const enabledStages = useMemo(
        () => PRODUCT_TRANSPORT_STAGES.filter((stage) => isStageEnabled(stage)),
        [isStageEnabled],
    );

    const transportPackagings =
        isStageEnabled("packaging_to_processing_site") ?
            product.productPackagings.filter(
                (packaging) => packaging["@id"] !== undefined && packaging.id !== undefined,
            )
        :   [];

    if (enabledStages.length === 0 && transportPackagings.length === 0) {
        return null;
    }

    return (
        <Box sx={{ padding: theme.spacing(3) }}>
            <Stack sx={{ gap: theme.spacing(2) }}>
                {enabledStages.map((stage) => (
                    <ProductTransportInlineSelector
                        key={stage}
                        product={product}
                        setProduct={setProduct}
                        stage={stage}
                        anchor={{ type: "product" }}
                        disabled={disabled}
                        label={stageLabel(stage, t)}
                        lineScore={resolveStageLineScore(stage, transportLineScores)}
                        resolvedTransport={resolveStageResolvedTransport(stage, resolvedTransports)}
                        scoreUnit={scoreUnit}
                        routePickerLayout
                        onMaterializationPendingChange={onTransportMaterializationPendingChange}
                    />
                ))}
                {transportPackagings.map((packaging, index) => {
                    const lineKey = getPackagingTransportLineScoreKey(packaging.id);
                    const name = packagingName(packaging);
                    const packagingAnchor =
                        packaging["@id"] !== undefined && packaging.id !== undefined ?
                            { "@id": packaging["@id"], id: packaging.id }
                        :   null;
                    const packagingStageLabel = t(
                        "product.v2.transport.stage.packaging_to_processing_site",
                        "Packaging to processing site",
                    );

                    return (
                        <ProductTransportInlineSelector
                            key={packagingKey(packaging, index)}
                            product={product}
                            setProduct={setProduct}
                            stage="packaging_to_processing_site"
                            anchor={{
                                type: "packaging",
                                value: packagingAnchor,
                            }}
                            disabled={disabled || packagingAnchor === null}
                            label={name === "" ? packagingStageLabel : `${packagingStageLabel} — ${name}`}
                            lineScore={lineKey === null ? null : (transportLineScores[lineKey] ?? null)}
                            resolvedTransport={
                                lineKey === null ? undefined : (
                                    resolvedTransports.find(
                                        (transport) =>
                                            transport.stage === "packaging_to_processing_site" &&
                                            transport.lineKey === lineKey,
                                    )
                                )
                            }
                            scoreUnit={scoreUnit}
                            routePickerLayout
                            onMaterializationPendingChange={onTransportMaterializationPendingChange}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};
